import { Suspense, useRef, useState, type KeyboardEvent } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { WebsiteLayout } from "@/components/layouts/WebsiteLayout";
import { FounderCallButton } from "@/components/website/FounderCall";
import { useSEO } from "@/hooks/useSEO";
import { homePageStructuredData } from "@shared/structuredData";
import { BrowserFrame } from "@/components/website/demo/BrowserFrame";
import { DEMO_RESERVED_HEIGHT } from "@/components/website/product-demo/DemoScreen";
import {
  DEMO_SCREENS,
  demoScreenHint,
  type DemoScreenName,
} from "@/components/website/product-demo/lazyScreens";
import {
  ArrowRight,
  Check,
  ChevronDown,
  Linkedin,
  ShieldCheck,
  Eye,
  UserCheck,
  Lock,
} from "lucide-react";

const LINKEDIN_URL = "https://www.linkedin.com/in/bram-van-gestel-6a52665/";

type ProductTab = {
  id: string;
  label: string;
  descriptor?: string;
  pain: string;
  /** Named source for an industry statistic used in `pain`. Rendered smaller, beneath it. */
  painSource?: string;
  promise: string;
  proof: string[];
  demoUrl: string;
  /**
   * The real product screen for this tab, mounted from the app's own
   * components and loaded as its own chunk when the tab is opened. Held by
   * name so the frame can also say what the visitor can do with that screen.
   */
  visual: DemoScreenName;
  /**
   * What that screen shows, in one factual line.
   *
   * The screen itself is client-rendered on purpose, so a crawler — and an AI
   * answer engine quoting the page — never sees it. This caption is the
   * indexable description of it, and it doubles as the alt text for anyone who
   * cannot see the picture.
   */
  visualCaption: string;
};

// Non-empty tuple type: the tab list always has a first entry to fall back to.
// Exported for the wiring test (marketingDemoWiring.test.ts), which checks
// every tab still points at a registered demo screen.
export const PRODUCT_TABS: [ProductTab, ...ProductTab[]] = [
  {
    id: "application-review",
    label: "Application Review",
    descriptor: "Candidate scorecard",
    pain: "200 applications, read one by one, between meetings.",
    promise:
      "Every applicant is read and scored across 8 signals. Each is ranked with reasons you can defend to any hiring manager.",
    proof: [
      "100% of applicants reviewed, none skimmed",
      "Shortlist ranked in minutes, not evenings",
      "Every score explained in plain language. No black box.",
    ],
    demoUrl: "app.talenture.ai/roles/platform-engineer/candidates/mira-n/scorecard",
    visual: "MatchScorecardScreen",
    visualCaption:
      "A candidate scorecard with an overall match, eight scored signals, the evidence behind each score, and the gaps to probe in the interview.",
  },
  {
    id: "pre-vetting",
    label: "Pre-vetting",
    pain: "Salary, notice period, start date: the deal-breakers that surface at the interview, after everyone spent the time.",
    promise: "The deal-breakers on the table before anyone books a call.",
    proof: [
      "Salary expectations checked against the role's range",
      "Notice period, availability and work preferences collected up front",
      "Candidates outside the range flagged with the reason, not silently cut",
    ],
    demoUrl: "app.talenture.ai/candidates/mira-n/preferences",
    visual: "CandidatePreferencesScreen",
    visualCaption:
      "A candidate's confirmed preferences: salary expectation against the role's range, notice period, earliest start date, location and remote preference. All collected before anyone books a call.",
  },
  {
    id: "scheduling",
    label: "Scheduling",
    pain: "A dozen messages to book one interview.",
    promise: "Qualified candidates self-book from your live calendar.",
    proof: [
      "Google Calendar and Outlook, conflicts checked in real time",
      "Time zones handled, no login needed for candidates",
      "Your working hours and buffers respected",
    ],
    demoUrl: "schedule.talenture.ai/meet/mira-n",
    visual: "SchedulingScreen",
    visualCaption:
      "The booking page a candidate sees: your real availability, in their time zone, with conflicts already filtered out and no account needed to pick a slot.",
  },
  {
    id: "interview-notes",
    label: "Interview Notes",
    pain: "You can take notes or be present. Not both.",
    promise:
      "Talenture joins the call, transcribes, and hands you a structured, decision-ready record.",
    proof: [
      "Works on Google Meet, Microsoft Teams, and Zoom",
      "Executive summary, fit insights, action items, sentiment",
      "Feeds the scorecard, so the decision is documented",
    ],
    demoUrl: "app.talenture.ai/candidates/mira-n/interview-notes",
    visual: "InterviewRecapScreen",
    visualCaption:
      "The record after an interview: an executive summary, fit insights against the role, action items, sentiment, and the full transcript. It is captured while you were listening.",
  },
  {
    id: "candidate-prep",
    label: "Candidate prep & feedback",
    pain: "Half of applicants never hear back. Your employer brand pays for it.",
    painSource:
      "Over half of job seekers report being ghosted by employers (Criteria Corp Candidate Experience Report).",
    promise: "Every candidate prepped before the interview and answered after it.",
    proof: [
      "Candidates practise on your real role and arrive ready",
      "Personalised, skill-based feedback for everyone who doesn't progress",
      "Every message is a draft until you approve it",
    ],
    demoUrl: "talenture.ai/mock-interview/senior-platform-engineer/results",
    visual: "MockInterviewFeedbackScreen",
    visualCaption:
      "The feedback a candidate receives after practising for your role: an overall score, a breakdown per competency, and specific, skill-based notes on what to improve.",
  },
];

const GUARDRAILS = [
  {
    icon: UserCheck,
    title: "It never decides.",
    body: "Talenture ranks, drafts, and books. Every interview and every rejection waits for you.",
  },
  {
    icon: Eye,
    title: "It shows its work.",
    body: "Every score breaks down across 8 signals: skills, experience, seniority, values, trajectory, location, salary, transferability. When the hiring manager asks \u201cwhy this candidate?\u201d, the answer is on one page.",
  },
  {
    icon: ShieldCheck,
    title: "It makes you more present, not less needed.",
    body: "No note-taking during interviews, no scheduling chase, no rejection backlog. The judgment work stays yours.",
  },
  {
    icon: Lock,
    title: "Your data, under your control.",
    body: "GDPR built in. Candidate data is yours to export or delete, any time.",
  },
];

const COMPARISON_ROWS = [
  {
    stage: "Reading applications",
    manual: "One by one, between meetings",
    talenture: "Every applicant read and scored automatically",
  },
  {
    stage: "Deal-breakers",
    manual: "Discovered in the first call",
    talenture: "Confirmed before anything is booked",
  },
  {
    stage: "Scheduling",
    manual: "A dozen messages per interview",
    talenture: "Candidates self-book from your live calendar",
  },
  {
    stage: "Interview notes",
    manual: "Your attention, split",
    talenture: "Structured record, captured for you",
  },
  {
    stage: "Rejections",
    manual: "A guilt pile you write at 6pm",
    talenture: "Personalised feedback, sent for you",
  },
];

/**
 * One tab's copy, plus its product screen when the tab is the open one.
 *
 * The copy renders whether or not the tab is open: a closed panel is hidden,
 * not absent, so a crawler that never clicks anything still reads all five
 * stages of the product. The screen itself is the expensive part — a real slice
 * of the app — so only the open tab mounts one, and the caption stands in for
 * the rest.
 */
function TabPanelBody({ tab, withVisual }: { tab: ProductTab; withVisual: boolean }) {
  const Visual = DEMO_SCREENS[tab.visual];
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="space-y-1">
          {tab.descriptor ? (
            <p
              className="text-sm font-semibold uppercase tracking-wide text-primary"
              data-testid={`text-tab-descriptor-${tab.id}`}
            >
              {tab.descriptor}
            </p>
          ) : null}
          <p className="text-base italic text-gray-500 dark:text-gray-400">{tab.pain}</p>
          {tab.painSource ? (
            <p
              className="text-xs text-gray-500 dark:text-gray-400"
              data-testid={`text-tab-pain-source-${tab.id}`}
            >
              {tab.painSource}
            </p>
          ) : null}
        </div>
        <p className="text-lg font-semibold text-gray-900 dark:text-white">{tab.promise}</p>
        <ul className="space-y-2">
          {tab.proof.map((point) => (
            <li key={point} className="flex items-start gap-2.5">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" aria-hidden="true" />
              <span className="text-sm text-gray-600 dark:text-gray-300">{point}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        {withVisual ? (
          <BrowserFrame url={tab.demoUrl} hint={demoScreenHint(tab.visual)}>
            <Suspense fallback={<div className={DEMO_RESERVED_HEIGHT.preview} />}>
              <Visual crop="preview" />
            </Suspense>
          </BrowserFrame>
        ) : null}
        <p
          className={`${withVisual ? "mt-4" : ""} text-sm text-gray-500 dark:text-gray-400`}
          data-testid={`text-tab-visual-caption-${tab.id}`}
        >
          {tab.visualCaption}
        </p>
      </div>
    </div>
  );
}

function ProductTabs() {
  const [activeId, setActiveId] = useState(PRODUCT_TABS[0].id);
  const activeTab = PRODUCT_TABS.find((tab) => tab.id === activeId) ?? PRODUCT_TABS[0];
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // WAI-ARIA vertical tablist: arrows move focus and selection, Home/End jump to the ends.
  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = PRODUCT_TABS.length - 1;
    let next: number | null = null;
    if (event.key === "ArrowDown") next = index === last ? 0 : index + 1;
    else if (event.key === "ArrowUp") next = index === 0 ? last : index - 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = last;
    if (next === null) return;
    event.preventDefault();
    const tab = PRODUCT_TABS[next];
    if (!tab) return;
    setActiveId(tab.id);
    tabRefs.current[next]?.focus();
  };

  return (
    <>
      {/* Mobile: accordion */}
      <div className="lg:hidden divide-y divide-gray-200 dark:divide-gray-700 border-y border-gray-200 dark:border-gray-700">
        {PRODUCT_TABS.map((tab) => {
          const expanded = tab.id === activeId;
          return (
            <div key={tab.id}>
              <h3>
                <button
                  type="button"
                  id={`accordion-tab-${tab.id}`}
                  onClick={() => setActiveId(expanded ? "" : tab.id)}
                  aria-expanded={expanded}
                  {...(expanded ? { "aria-controls": `panel-${tab.id}` } : {})}
                  className="flex w-full items-center justify-between gap-4 py-4 text-left"
                  data-testid={`accordion-tab-${tab.id}`}
                >
                  <span className="text-base font-semibold text-gray-900 dark:text-white">
                    {tab.label}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`}
                    aria-hidden="true"
                  />
                </button>
              </h3>
              {expanded && (
                <div
                  id={`panel-${tab.id}`}
                  role="region"
                  aria-labelledby={`accordion-tab-${tab.id}`}
                  className="pb-8"
                >
                  <TabPanelBody tab={tab} withVisual />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop: vertical tab list + panel */}
      <div className="hidden lg:grid lg:grid-cols-[minmax(0,260px)_minmax(0,1fr)] lg:gap-12">
        <div
          role="tablist"
          aria-orientation="vertical"
          aria-label="What Talenture runs between job post and first interview"
          className="flex flex-col gap-1 border-l border-gray-200 dark:border-gray-700"
        >
          {PRODUCT_TABS.map((tab, index) => {
            const selected = tab.id === activeId;
            return (
              <button
                key={tab.id}
                ref={(node) => {
                  tabRefs.current[index] = node;
                }}
                type="button"
                role="tab"
                id={`tab-${tab.id}`}
                aria-selected={selected}
                aria-controls={`tabpanel-${tab.id}`}
                tabIndex={selected ? 0 : -1}
                onKeyDown={(event) => onTabKeyDown(event, index)}
                onClick={() => setActiveId(tab.id)}
                className={`-ml-px border-l-2 py-3 pl-5 pr-3 text-left text-base transition-colors ${
                  selected
                    ? "border-primary font-semibold text-primary"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-900 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-white"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        {/*
          Every panel is rendered and the closed ones are hidden, rather than
          left unrendered: the four stages a visitor has not clicked are still
          part of what this page says, and a crawler clicks nothing.
        */}
        <div className="min-w-0">
          {PRODUCT_TABS.map((tab) => (
            <div
              key={tab.id}
              role="tabpanel"
              id={`tabpanel-${tab.id}`}
              aria-labelledby={`tab-${tab.id}`}
              hidden={tab.id !== activeTab.id}
            >
              <TabPanelBody tab={tab} withVisual={tab.id === activeTab.id} />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default function CompanyHomePage() {
  useSEO({
    title: "AI Recruiting Software That Works With Your ATS | Talenture.ai",
    description:
      "Talenture plugs into your ATS and runs every step from job post to booked interview: applicants scored, deal-breakers confirmed, interviews booked. You decide.",
    keywords: [
      "AI recruiting software",
      "recruitment automation",
      "AI hiring platform",
      "automate hiring",
      "AI candidate screening",
      "ATS AI layer",
      "pre-vetted candidates",
    ],
    ogTitle: "AI Recruiting Software That Works With Your ATS | Talenture.ai",
    ogDescription:
      "Talenture plugs into your ATS and runs the work between job post and first interview: every applicant read and scored, deal-breakers confirmed, candidates prepped, interviews booked. You decide.",
    ogUrl: "https://www.talenture.ai/",
    ogImage: "https://www.talenture.ai/og-image.png",
    canonical: "https://www.talenture.ai/",
    structuredData: homePageStructuredData(),
  });

  return (
    <WebsiteLayout variant="company">
      {/* 1 — Hero */}
      <section className="relative overflow-hidden py-20 lg:py-28">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-50 to-white dark:from-gray-900 dark:to-gray-950" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h1
              className="mb-6 text-4xl font-bold text-gray-900 dark:text-white md:text-5xl lg:text-6xl"
              data-testid="text-hero-headline"
            >
              Not a pile of CVs. A shortlist ready to interview.
            </h1>
            <p
              className="mx-auto mb-8 max-w-3xl text-xl text-gray-600 dark:text-gray-300"
              data-testid="text-hero-subheadline"
            >
              Talenture works with your ATS and runs the work between job post and first interview:
              it reads and scores every applicant, confirms salary and availability up front, preps
              the candidates, and books them into your calendar. You review, you decide, you hire.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Button size="lg" className="text-lg px-8" data-testid="button-hero-primary" asChild>
                <a href="#sample-scorecard">See a sample scorecard</a>
              </Button>
              <FounderCallButton
                size="lg"
                variant="outline"
                className="text-lg px-8 border-gray-300 text-gray-900 hover:bg-gray-100 dark:border-gray-600 dark:text-white dark:hover:bg-gray-800"
                data-testid="button-hero-founder-call"
              />
            </div>
            <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
              Works with your ATS · GDPR built in · Every score explained
            </p>
          </div>
        </div>
      </section>

      {/* 2 — Proof strip */}
      <section className="border-y border-gray-200 bg-white py-10 dark:border-gray-800 dark:bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 md:grid-cols-3 md:gap-10">
            <div className="flex items-start gap-4" data-testid="proof-founder">
              <img
                src="/bram.jpg"
                alt="Bram van Gestel, founder of Talenture"
                width={64}
                height={64}
                loading="lazy"
                decoding="async"
                className="h-16 w-16 flex-shrink-0 rounded-full object-cover"
              />
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">
                  Built by Bram van Gestel
                </p>
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                  data-testid="link-founder-linkedin"
                >
                  <Linkedin className="h-4 w-4" aria-hidden="true" />
                  LinkedIn
                </a>
              </div>
            </div>

            <div data-testid="proof-taxonomy">
              <p className="font-semibold text-gray-900 dark:text-white">
                Matching built on O*NET and ESCO
              </p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                The skills taxonomies used by the U.S. Department of Labor and the European
                Commission.
              </p>
            </div>

            <div data-testid="proof-design-partners">
              <p className="font-semibold text-gray-900 dark:text-white">
                Now onboarding our first design partners.
              </p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Your first role is on us, with up to 50 candidates free.
              </p>
              <Link
                href="/company-auth"
                className="mt-2 inline-flex items-center text-sm font-medium text-primary hover:underline"
                data-testid="link-proof-claim-free-role"
              >
                Claim your free role
                <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 3 — Pain stanza */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl font-bold text-gray-900 dark:text-white md:text-4xl"
            data-testid="text-pain-headline"
          >
            You know this week.
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-gray-600 dark:text-gray-300">
            Twenty-three reqs. Two hundred applications on the newest one. And you're the one who
            reads them all, chases the scheduling, and writes the rejections between the interviews
            you're actually paid for.
          </p>
          <p className="mt-5 text-lg font-medium leading-relaxed text-gray-900 dark:text-white">
            Talenture does that part. You walk into every interview with a shortlist you can defend
            and the prep already done.
          </p>
        </div>
      </section>

      {/* 4 — Product tabs */}
      <section className="bg-gray-50 py-20 dark:bg-gray-900/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ProductTabs />
        </div>
      </section>

      {/* 5 — Sample scorecard */}
      <section id="sample-scorecard" className="scroll-mt-20 py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2
              className="text-3xl font-bold text-gray-900 dark:text-white md:text-4xl"
              data-testid="text-scorecard-headline"
            >
              See what lands on your desk.
            </h2>
            <p className="mt-5 text-lg text-gray-600 dark:text-gray-300">
              Every applicant in your ATS arrives scored: the ranking, the reasons behind it, and
              the gaps to probe in the interview. You still make every call.
            </p>
            {/* The scorecard itself is rendered directly below, so this section
                links nowhere — the founder call is the only action worth offering
                to someone already looking at the thing. */}
            <div className="mt-8 flex justify-center">
              <FounderCallButton size="lg" className="text-lg px-8" data-testid="button-scorecard-cta" />
            </div>
          </div>

          <div className="mt-14">
            <BrowserFrame
              url="app.talenture.ai/roles/platform-engineer/candidates/mira-n/scorecard"
              hint={demoScreenHint("MatchScorecardScreen")}
            >
              <Suspense fallback={<div className={DEMO_RESERVED_HEIGHT.lead} />}>
                <DEMO_SCREENS.MatchScorecardScreen crop="lead" />
              </Suspense>
            </BrowserFrame>
          </div>
        </div>
      </section>

      {/* 6 — Trust & guardrails */}
      <section className="bg-gray-900 py-20 dark:bg-black">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2
            className="text-center text-3xl font-bold text-white md:text-4xl"
            data-testid="text-guardrails-headline"
          >
            AI does the work. You make every call.
          </h2>
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            {GUARDRAILS.map((item) => (
              <div key={item.title} className="flex gap-4" data-testid={`guardrail-${item.title}`}>
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-white/10">
                  <item.icon className="h-5 w-5 text-violet-300" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-gray-300">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7 — The manual way vs. with Talenture */}
      <section className="py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm dark:border-gray-700">
            <table className="w-full min-w-[640px] text-left text-sm">
              <caption className="sr-only">
                What happens between job post and first interview, the manual way compared with
                Talenture
              </caption>
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                  <th scope="col" className="w-1/3 px-5 py-4 font-semibold text-gray-900 dark:text-white">
                    Between job post and first interview
                  </th>
                  <th scope="col" className="px-5 py-4 font-semibold text-gray-500 dark:text-gray-400">
                    The manual way
                  </th>
                  <th scope="col" className="bg-primary/5 px-5 py-4 font-semibold text-primary dark:bg-primary/10">
                    With Talenture
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {COMPARISON_ROWS.map((row) => (
                  <tr key={row.stage} data-testid={`comparison-row-${row.stage}`}>
                    <th scope="row" className="px-5 py-4 font-medium text-gray-900 dark:text-white">
                      {row.stage}
                    </th>
                    <td className="px-5 py-4 text-gray-500 dark:text-gray-400">{row.manual}</td>
                    <td className="bg-primary/5 px-5 py-4 text-gray-900 dark:bg-primary/10 dark:text-white">
                      {row.talenture}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 8 — Pricing teaser */}
      <section className="bg-gray-50 py-20 dark:bg-gray-900/50">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-2">
            <div
              className="relative flex flex-col rounded-2xl border-2 border-primary bg-white p-8 shadow-xl dark:bg-gray-800"
              data-testid="card-pricing-ats-layer"
            >
              <span className="mb-3 inline-flex w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
                Start here if you have an ATS
              </span>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Works with your ATS
              </h3>
              <p className="mt-3 flex-1 text-gray-600 dark:text-gray-300">
                Keep your system of record. Talenture processes every candidate that flows in and
                runs the full workflow on top. From €1 per candidate processed.
              </p>
              <Link
                href="/company/pricing/ats-layer"
                className="mt-6 inline-flex items-center font-medium text-primary hover:underline"
                data-testid="link-pricing-ats-layer"
              >
                View ATS Layer pricing
                <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
              </Link>
            </div>

            <div
              className="flex flex-col rounded-2xl border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-700 dark:bg-gray-800"
              data-testid="card-pricing-hiring-app"
            >
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                No ATS? Run Talenture as your hiring app.
              </h3>
              <p className="mt-3 flex-1 text-gray-600 dark:text-gray-300">
                Everything in one place, candidate tracking included. €299 per booked interview. €0
                until then.
              </p>
              <Link
                href="/company/pricing/hiring-app"
                className="mt-6 inline-flex items-center font-medium text-primary hover:underline"
                data-testid="link-pricing-hiring-app"
              >
                View Hiring App pricing
                <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 9 — Final CTA */}
      <section id="get-started" className="bg-primary py-20 dark:bg-primary/90">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2
            className="text-3xl font-bold text-white md:text-4xl"
            data-testid="text-final-cta-headline"
          >
            Start with one role.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-white/85">
            Connect your ATS or just forward the applications. See the scorecards land. Decide from
            there. Your first role is on us, with up to 50 candidates free.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button
              size="lg"
              variant="secondary"
              className="text-lg px-10"
              data-testid="button-final-cta-claim"
              asChild
            >
              <Link href="/company-auth">Claim your free role</Link>
            </Button>
            <FounderCallButton
              size="lg"
              variant="outline"
              className="border-white/40 bg-transparent text-lg px-8 text-white hover:bg-white/10 hover:text-white"
              data-testid="button-final-cta-founder-call"
            />
          </div>
        </div>
      </section>
    </WebsiteLayout>
  );
}
